/**
 * NLLB-200 Translation — client-side entity executor.
 *
 * Wraps Meta's NLLB-200-distilled-600M via `@huggingface/transformers`
 * (transformers.js / ONNX Runtime Web) — a WASM model that runs entirely in
 * the user's browser. Exported as a definition object (entity config in
 * `packages/entities/src/integrations/nllb-translation/config.ts`); the
 * webapp registers it at startup via
 * `client/src/lib/register-client-entities.ts`.
 *
 * Design follows the standard client-side executor pattern:
 *   - The entity ID is a string literal here so the client never imports
 *     `@fias/entities` (which has server-only transitive deps). The
 *     architecture test `client-entity-registry-mirrors-entity-config.test.ts`
 *     pins it against the canonical config.
 *   - Heavy deps (transformers.js, franc) are lazily `import()`-ed on the
 *     first invocation, NOT at module load — registration must stay cheap
 *     because the definitions are imported at app startup for everyone,
 *     including users who never translate.
 *   - Calls are serialized on a tail promise: the ONNX session is not safe
 *     for concurrent inference, and the registry can be reached from both
 *     internal arches and the plugin bridge.
 *
 * Source language: auto-detected on-device with `franc` (n-gram based, no
 * model download) when `sourceLang` is omitted — mirroring the auto-detect
 * the credit-billed LLM path gets for free. NLLB itself requires an explicit
 * source FLORES code, so detection is a hard prerequisite, not a nicety.
 *
 * Model weights (~600 MB at q8) are fetched from the Hugging Face Hub CDN on
 * first use and cached in the browser (IndexedDB) by transformers.js; the
 * host app's CSP must allow `huggingface.co` (and the ONNX wasm backend
 * host). First translation therefore pays a one-time download; subsequent
 * ones are fast.
 */

import type { ClientEntityDefinition } from './types';

/**
 * Entity ID of the NLLB translation client-side entity.
 *
 * SYNC NOTE: must match `NLLB_TRANSLATION_ENTITY_ID` in
 * `packages/entities/src/integrations/nllb-translation/config.ts`. The
 * architecture test `client-entity-registry-mirrors-entity-config.test.ts`
 * enforces this.
 */
export const NLLB_TRANSLATION_ENTITY_ID = 'ent_intg_nllb_xlate_000000000000';

export interface NllbTranslationInput {
  /** Text to translate. Trimmed; must be non-empty and ≤ NLLB_MAX_INPUT_CHARS. */
  text: string;
  /** Target language as a FLORES-200 code, e.g. `'fra_Latn'`, `'spa_Latn'`. */
  targetLang: string;
  /**
   * Source language as a FLORES-200 code. Omit to auto-detect on-device.
   * Supplying it skips detection (faster, and avoids misdetection on short
   * or mixed-language input).
   */
  sourceLang?: string;
}

export interface NllbTranslationResult {
  /** The translated text. */
  translation: string;
  /** The FLORES-200 source code actually used (detected, or the one passed in). */
  detectedSourceLang: string;
}

/**
 * Input cap. NLLB's effective context is ~512 tokens; beyond that the
 * tokenizer truncates silently, so we bound input well under that to avoid
 * surprising the user with a half-translated paragraph. Long documents
 * should be chunked by the caller (a future enhancement could chunk here).
 */
export const NLLB_MAX_INPUT_CHARS = 2000;

/** transformers.js-compatible ONNX build of NLLB-200-distilled-600M on the HF Hub. */
const NLLB_MODEL_ID = 'Xenova/nllb-200-distilled-600M';

/**
 * Immutable HF Hub commit pinned for the model download (supply-chain).
 * Without this, transformers.js resolves the *mutable* `main` revision, so a
 * repo compromise or a silent re-upload would swap the weights/tokenizer we
 * fetch. The weights are data to onnxruntime-web (not executable JS, so this
 * is not a code-exec vector) but an unpinned revision still means adversarial
 * or corrupted *translations*. Bump deliberately when intentionally moving to
 * newer weights. Source: huggingface.co/Xenova/nllb-200-distilled-600M `main`.
 */
const NLLB_MODEL_REVISION = '261c31d1a5732c67cdd16d80e8d6088507c7ccea';

/**
 * Weight quantization. `q8` (int8) is the quality/size sweet spot for the
 * 600M model in the browser (~600 MB download). transformers.js accepts a
 * single dtype string for all encoder/decoder components.
 */
const NLLB_DTYPE = 'q8';

/**
 * franc's ISO 639-3 output → NLLB FLORES-200 code. Keyed by exactly what
 * franc emits (note the individual-language codes: Mandarin is `'cmn'`,
 * Standard Arabic `'arb'`, Western Persian `'pes'`). `franc(..., { only })`
 * is restricted to these keys so detection can never return a language we
 * can't then translate.
 */
export const FRANC_TO_FLORES: Readonly<Record<string, string>> = {
  eng: 'eng_Latn',
  spa: 'spa_Latn',
  fra: 'fra_Latn',
  deu: 'deu_Latn',
  ita: 'ita_Latn',
  por: 'por_Latn',
  nld: 'nld_Latn',
  pol: 'pol_Latn',
  ron: 'ron_Latn',
  ces: 'ces_Latn',
  slk: 'slk_Latn',
  hun: 'hun_Latn',
  swe: 'swe_Latn',
  nob: 'nob_Latn',
  dan: 'dan_Latn',
  fin: 'fin_Latn',
  cat: 'cat_Latn',
  hrv: 'hrv_Latn',
  rus: 'rus_Cyrl',
  ukr: 'ukr_Cyrl',
  bul: 'bul_Cyrl',
  srp: 'srp_Cyrl',
  ell: 'ell_Grek',
  heb: 'heb_Hebr',
  arb: 'arb_Arab',
  pes: 'pes_Arab',
  urd: 'urd_Arab',
  hin: 'hin_Deva',
  ben: 'ben_Beng',
  tam: 'tam_Taml',
  tel: 'tel_Telu',
  cmn: 'zho_Hans',
  jpn: 'jpn_Jpan',
  kor: 'kor_Hang',
  vie: 'vie_Latn',
  tha: 'tha_Thai',
  ind: 'ind_Latn',
  tur: 'tur_Latn',
};

const SUPPORTED_FRANC_CODES = Object.keys(FRANC_TO_FLORES);

/**
 * FLORES-200 code shape: a 3-letter ISO 639-3 language subtag, an underscore,
 * then a 4-letter ISO 15924 script subtag (title-case), e.g. `spa_Latn`,
 * `zho_Hans`, `arb_Arab`. NLLB's tokenizer keys its language tokens on exactly
 * this form; anything else makes transformers.js throw an opaque error deep in
 * inference. We validate the *shape* rather than an exhaustive 200-entry
 * allowlist so the full model surface stays reachable while typos and
 * obviously-wrong values (e.g. a bare `'es'`) fail fast with a clear message.
 */
const FLORES_CODE_RE = /^[a-z]{3}_[A-Z][a-z]{3}$/;

function assertFloresCode(code: string, field: string): void {
  if (!FLORES_CODE_RE.test(code)) {
    throw new Error(
      `nllb-translation: ${field} must be a FLORES-200 code like 'spa_Latn' (got '${code}')`,
    );
  }
}

/** Thrown when on-device detection can't resolve the source language. */
export class NllbLanguageDetectionError extends Error {
  constructor() {
    super(
      'Could not detect the source language (text too short or ambiguous). ' +
        'Specify the source language explicitly and try again.',
    );
    this.name = 'NllbLanguageDetectionError';
  }
}

type TransformersModule = typeof import('@huggingface/transformers');
/** Minimal call signature of a transformers.js translation pipeline. */
type TranslateFn = (
  text: string,
  options: { src_lang: string; tgt_lang: string },
) => Promise<Array<{ translation_text: string }>>;

let translatorPromise: Promise<TranslateFn> | null = null;

function loadTranslator(): Promise<TranslateFn> {
  if (!translatorPromise) {
    // On failure (chunk-load error, CDN hiccup, OOM) clear the cached
    // rejected promise so the next call retries from scratch.
    translatorPromise = (async () => {
      const transformers = (await import('@huggingface/transformers')) as TransformersModule;
      // Always fetch the model from the HF Hub CDN — the app serves no local
      // /models route, so a local-first lookup would just 404 then fall back.
      transformers.env.allowLocalModels = false;
      const pipe = await transformers.pipeline('translation', NLLB_MODEL_ID, {
        dtype: NLLB_DTYPE,
        revision: NLLB_MODEL_REVISION,
      });
      return pipe as unknown as TranslateFn;
    })().catch((err) => {
      translatorPromise = null;
      throw err;
    });
  }
  return translatorPromise;
}

type FrancFn = (value: string, options?: { only?: string[]; minLength?: number }) => string;

let francPromise: Promise<FrancFn> | null = null;

function loadFranc(): Promise<FrancFn> {
  if (!francPromise) {
    francPromise = import('franc')
      .then((m) => m.franc as FrancFn)
      .catch((err) => {
        francPromise = null;
        throw err;
      });
  }
  return francPromise;
}

// Serialize all calls — see file header. `.catch(() => {})` keeps one failed
// call from poisoning the chain for subsequent callers.
let tail: Promise<void> = Promise.resolve();

export async function nllbTranslationExecutor(
  input: NllbTranslationInput,
): Promise<NllbTranslationResult> {
  if (!input || typeof input.text !== 'string') {
    throw new Error('nllb-translation: input.text must be a string');
  }
  const text = input.text.trim();
  if (!text) {
    throw new Error('nllb-translation: input.text is empty');
  }
  if (text.length > NLLB_MAX_INPUT_CHARS) {
    throw new Error(
      `nllb-translation: text exceeds ${NLLB_MAX_INPUT_CHARS}-char limit (got ${text.length})`,
    );
  }
  if (typeof input.targetLang !== 'string' || !input.targetLang) {
    throw new Error('nllb-translation: input.targetLang (FLORES-200 code) is required');
  }
  assertFloresCode(input.targetLang, 'targetLang');
  if (input.sourceLang !== undefined) {
    if (typeof input.sourceLang !== 'string') {
      throw new Error('nllb-translation: input.sourceLang must be a FLORES-200 code string');
    }
    assertFloresCode(input.sourceLang, 'sourceLang');
  }

  const run = tail.then(async () => {
    let sourceLang = input.sourceLang;
    if (!sourceLang) {
      const franc = await loadFranc();
      // Use franc's default minLength (10): below it franc returns 'und', which
      // we surface as NllbLanguageDetectionError so the caller can prompt for an
      // explicit source. Forcing minLength: 1 would coerce a confident best-in-set
      // guess for 1–2 char / ambiguous input and translate from the wrong source.
      const iso = franc(text, { only: SUPPORTED_FRANC_CODES });
      const mapped = FRANC_TO_FLORES[iso];
      if (!mapped) {
        throw new NllbLanguageDetectionError();
      }
      sourceLang = mapped;
    }

    const translate = await loadTranslator();
    const output = await translate(text, { src_lang: sourceLang, tgt_lang: input.targetLang });
    // Fail loud on unexpected model output ([] or [{}]): a silent '' is
    // indistinguishable from a genuine empty translation and hides model/CDN
    // faults from the caller.
    const translation = output?.[0]?.translation_text;
    if (typeof translation !== 'string' || translation.length === 0) {
      throw new Error('nllb-translation: model returned no translation');
    }
    return { translation, detectedSourceLang: sourceLang };
  });
  tail = run.then(
    () => {},
    () => {},
  );
  return run;
}

/**
 * Warm the model so the first user-triggered translation avoids the one-time
 * download/initialization latency. No-op if already loaded. Optional — call
 * from a component mount when you expect the feature to be used soon.
 */
export async function prefetchNllbTranslation(): Promise<void> {
  await loadTranslator();
}

/**
 * Registration input for the webapp's client-entity registry. Rate limit and
 * permission must mirror `nllbTranslationConfig.pluginInvocable` in
 * `@fias/entities` (pinned by
 * `client-entity-registry-mirrors-entity-config.test.ts`).
 */
export const nllbTranslationDefinition = {
  entityId: NLLB_TRANSLATION_ENTITY_ID,
  rateLimitPerMinute: 20,
  pluginPermission: 'entities:client_invoke',
  executor: nllbTranslationExecutor,
} as const satisfies ClientEntityDefinition<NllbTranslationInput, NllbTranslationResult>;
